import React, { useState, useEffect, useMemo } from 'react';
import {
  Row, Col, Card, CardBody, Button, Table, Input, Label, Alert, Spinner,
  Modal, ModalHeader, ModalBody, ModalFooter, Badge, FormGroup
} from 'reactstrap';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/ApiService';
import { toast } from 'react-toastify';

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'var(--background-secondary)',
    borderColor: state.isFocused ? 'var(--accent-color)' : 'var(--border-color)',
    color: 'var(--text-primary)',
    '&:hover': { borderColor: 'var(--accent-color)' }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--background-primary)',
    border: '1px solid var(--border-color)',
    zIndex: 9999
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? 'var(--accent-color)' : state.isFocused ? 'var(--hover-color)' : 'transparent',
    color: state.isSelected ? 'var(--background-primary)' : 'var(--text-primary)'
  }),
  singleValue: (provided) => ({ ...provided, color: 'var(--text-primary)' }),
  input: (provided) => ({ ...provided, color: 'var(--text-primary)' }),
  placeholder: (provided) => ({ ...provided, color: 'var(--text-secondary)' })
};

const mono = { fontFamily: 'monospace', fontSize: 13 };

const statusColour = (status) => (status === 'active' ? 'success' : 'secondary');

// What the customer actually receives. Everything needed to query the data is in this one
// block: the endpoint, the credentials, the read surface and the rules that stop an agent
// producing a confident wrong number. It needs no software installed -- the credential is
// plain HTTPS basic auth -- so "a credential and a prompt" is literally true.
// Kept in step with docs/agent-data-access.md in the API repo, which is the full reference.
const buildAgentPrompt = (connection, username, password, mediaToken) => `You have read-only \
access to our Foocus sponsorship analytics. Query it over HTTPS -- no tools to install.

Endpoint: POST https://${connection?.host || '<host>'}:${connection?.port || '8443'}/
Auth:     HTTP basic, username ${username}, password ${password}
Body:     the SQL itself. Append " FORMAT JSON" to get JSON back.

The data is brand exposure our organisation generated for its sponsoring partners -- TV,
press, Instagram and Facebook. You only ever see rows our organisation is entitled to.
Query these, preferring the highest one that answers the question:
  agent_sponsorship_health_v1      -- headline scorecard per month, partner and platform
  partner_exposure_by_month / _by_week / _by_day  -- breakdowns: channel, ownership,
                                      placement, event, country
  partner_exposure                 -- raw facts, one row per content item per partner
  agent_exposure_evidence_v1       -- images evidencing an exposure (see below)

Rules -- these prevent almost every wrong answer:
1. media_value and exposures sum across anything. audience_reach NEVER sums across
   platforms (source) -- each platform counts the same people differently. Group by source.
2. Press has no audience_reach: its readership is in opportunities_to_see, which is
   opportunities, not people. Never present opportunities_to_see as reach.
3. A NULL engagement_rate means the platform reports no engagement signal -- say "not
   measured", never zero. Recompute rates from sum(engagements) / sum(audience_reach);
   never average them.
4. On the raw partner_exposure table, audience_reach repeats for every partner sharing one
   broadcast or post. Across partners, deduplicate:
   SELECT sum(r) FROM (SELECT dedup_key, max(audience_reach) AS r
                       FROM partner_exposure GROUP BY dedup_key).
5. Qualify figures with the method columns: reach_method ('measured' is real;
   'follower_proxy'/'modelled' are estimates; 'not_reported' means the platform said
   nothing), rate_source ('platform_default' = standard rates, not contracted), and
   placement_method ('feature_off' = not measured, never "the asset generated nothing").
6. ownership separates owned (our channels), tracked (named accounts we follow) and earned
   (third parties). Different achievements -- never merge them silently.
7. The newest month is still in progress. Exclude it from comparisons with
   event_month < toStartOfMonth(today()).
8. Ignore tenant_metrics, agent_exposure_monthly_v1 and agent_exposure_weekly_v1 -- they
   are the previous generation, scheduled for removal.
9. To show what an exposure looked like: take example_media_id (or example_debug_media_id,
   which has the brand drawn in a box) from a row, or any media_id from
   agent_exposure_evidence_v1, and fetch
     GET ${connection?.media_endpoint || 'https://<api-host>/api/agent/media/{media_id}'}
     Authorization: Bearer ${mediaToken || '<media token>'}
   A 302 serves the image. A JSON body with access='external_link' means it is
   publisher-hosted: fetch origin_url directly and note availability is not guaranteed.

All amounts are in NOK. Show me the SQL you ran alongside any figure.

To start: tell me what period each platform covers and which partners appear, then give me
last month's scorecard -- media value, exposures, reach per platform, engagement rate --
compared with the month before.`;

const AdminAgentAccess = () => {
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [connection, setConnection] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [busy, setBusy] = useState(false);

  // The once-shown customer credential modal only opens when the operator asks for it.
  // Staff connect through the MCP server, which fetches the credential from the API itself.
  const [showCustomerPrompt, setShowCustomerPrompt] = useState(false);
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  // Personal MCP token for the signed-in admin, shown exactly once.
  const [mcpToken, setMcpToken] = useState(null);
  const [mcpCopied, setMcpCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await ApiService.getAgentAccess({ status: statusFilter === 'all' ? undefined : statusFilter });
      setRows(res.data?.data || []);
      setConnection(res.data?.connection || null);
    } catch (err) {
      setError(t('agent_access_load_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter]);

  useEffect(() => {
    ApiService.getAllTenants()
      .then((res) => setTenants(res.data || []))
      .catch(() => setTenants([]));
  }, []);

  const tenantOptions = useMemo(
    () => tenants.map((tn) => ({ value: tn.id, label: `${tn.name} (${tn.id})` })),
    [tenants]
  );

  const handleProvision = async () => {
    if (!selectedTenant) return;
    setBusy(true);
    try {
      const res = await ApiService.createAgentAccess({ tenant_id: selectedTenant.value });
      if (showCustomerPrompt) {
        setIssued({
          username: res.data.access.ch_username,
          password: res.data.password,
          media_token: res.data.media_token,
          connection: res.data.connection
        });
        setCopied(false);
      } else {
        toast.success(t('agent_access_provisioned'));
      }
      setSelectedTenant(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('agent_access_provision_failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleRotate = async (row) => {
    setBusy(true);
    try {
      const res = await ApiService.rotateAgentAccess(row.id);
      if (showCustomerPrompt) {
        setIssued({
          username: res.data.access.ch_username,
          password: res.data.password,
          media_token: res.data.media_token,
          connection: res.data.connection
        });
        setCopied(false);
      } else {
        toast.success(t('agent_access_rotated_ok'));
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('agent_access_rotate_failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!confirmRevoke) return;
    setBusy(true);
    try {
      await ApiService.revokeAgentAccess(confirmRevoke.id);
      toast.success(t('agent_access_revoked'));
      setConfirmRevoke(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('agent_access_revoke_failed'));
    } finally {
      setBusy(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
  };

  const handleConnectClaude = async (row) => {
    setBusy(true);
    try {
      const res = await ApiService.createAgentMcpToken(row.id);
      setMcpToken({ ...res.data, tenant_label: `${row.tenant_name} (${row.tenant_id})` });
      setMcpCopied(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('agent_access_mcp_token_failed'));
    } finally {
      setBusy(false);
    }
  };

  const copyMcp = (text) => {
    navigator.clipboard.writeText(text);
    setMcpCopied(true);
  };

  return (
    <div style={{ color: 'var(--text-primary)' }}>
      <Row>
        <Col>
          <h4 style={{ color: 'var(--text-primary)' }}>{t('agent_access_title')}</h4>
          <p style={{ color: 'var(--text-secondary)' }}>{t('agent_access_description')}</p>
        </Col>
      </Row>

      {error && <Alert color="danger">{error}</Alert>}

      <Card style={{ backgroundColor: 'var(--background-secondary)', borderColor: 'var(--border-color)' }}>
        <CardBody>
          <Row className="align-items-end">
            <Col md={5}>
              <FormGroup>
                <Label style={{ color: 'var(--text-primary)' }}>{t('agent_access_tenant')}</Label>
                <Select
                  options={tenantOptions}
                  value={selectedTenant}
                  onChange={setSelectedTenant}
                  styles={customSelectStyles}
                  placeholder={t('agent_access_select_tenant')}
                  isClearable
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label style={{ color: 'var(--text-primary)' }}>{t('agent_access_status')}</Label>
                <Input
                  type="select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    backgroundColor: 'var(--background-secondary)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)'
                  }}
                >
                  <option value="all">{t('agent_access_status_all')}</option>
                  <option value="active">{t('agent_access_status_active')}</option>
                  <option value="revoked">{t('agent_access_status_revoked')}</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Button color="primary" disabled={!selectedTenant || busy} onClick={handleProvision}>
                  {busy ? <Spinner size="sm" /> : t('agent_access_provision')}
                </Button>
              </FormGroup>
            </Col>
          </Row>
          <FormGroup check className="mb-3">
            <Label check style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
              <Input type="checkbox" checked={showCustomerPrompt}
                     onChange={(e) => setShowCustomerPrompt(e.target.checked)} />{' '}
              {t('agent_access_show_customer_prompt')}
            </Label>
          </FormGroup>

          {loading ? (
            <div className="text-center p-4"><Spinner /></div>
          ) : (
            <Table responsive style={{ color: 'var(--text-primary)' }}>
              <thead>
                <tr style={{ color: 'var(--text-secondary)' }}>
                  <th>{t('agent_access_tenant')}</th>
                  <th>{t('agent_access_username')}</th>
                  <th>{t('agent_access_status')}</th>
                  <th>{t('agent_access_mcp_ready')}</th>
                  <th>{t('agent_access_created')}</th>
                  <th>{t('agent_access_rotated')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={7} style={{ color: 'var(--text-secondary)' }}>{t('agent_access_none')}</td></tr>
                )}
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.tenant_name} <span style={{ color: 'var(--text-secondary)' }}>({row.tenant_id})</span></td>
                    <td style={mono}>{row.ch_username}</td>
                    <td><Badge color={statusColour(row.status)}>{row.status}</Badge></td>
                    <td>
                      {row.status === 'active' && (
                        row.has_stored_credential
                          ? <Badge color="success">{t('agent_access_stored')}</Badge>
                          : <Badge color="warning">{t('agent_access_needs_rotation')}</Badge>
                      )}
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{row.created_at?.slice(0, 10) || '—'}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{row.rotated_at?.slice(0, 10) || '—'}</td>
                    <td className="text-right">
                      {row.status === 'active' && (
                        <>
                          <Button size="sm" color="primary" outline
                                  disabled={busy || !row.has_stored_credential}
                                  title={row.has_stored_credential ? '' : t('agent_access_needs_rotation_hint')}
                                  onClick={() => handleConnectClaude(row)} className="mr-2">
                            {t('agent_access_connect_claude')}
                          </Button>
                          <Button size="sm" color="secondary" disabled={busy}
                                  onClick={() => handleRotate(row)} className="mr-2">
                            {t('agent_access_rotate')}
                          </Button>
                          <Button size="sm" color="danger" disabled={busy}
                                  onClick={() => setConfirmRevoke(row)}>
                            {t('agent_access_revoke')}
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Credential is shown once. Closing is gated on copying, because the only remedy
          for a lost password is a rotation. */}
      <Modal isOpen={!!issued} backdrop="static" keyboard={false} centered size="lg">
        <ModalHeader style={{ color: 'var(--text-primary)' }}>{t('agent_access_issued_title')}</ModalHeader>
        <ModalBody>
          <Alert color="warning">{t('agent_access_issued_warning')}</Alert>

          <Label style={{ color: 'var(--text-primary)' }}>{t('agent_access_username')}</Label>
          <Input readOnly value={issued?.username || ''} style={{
            ...mono, backgroundColor: 'var(--background-secondary)',
            color: 'var(--text-primary)', borderColor: 'var(--border-color)'
          }} />

          <Label className="mt-3" style={{ color: 'var(--text-primary)' }}>{t('agent_access_password')}</Label>
          <Input readOnly value={issued?.password || ''} onFocus={(e) => e.target.select()} style={{
            ...mono, backgroundColor: 'var(--background-secondary)',
            color: 'var(--text-primary)', borderColor: 'var(--border-color)'
          }} />

          <Label className="mt-3" style={{ color: 'var(--text-primary)' }}>{t('agent_access_media_token')}</Label>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 8 }}>
            {t('agent_access_media_token_help')}
          </p>
          <Input readOnly value={issued?.media_token || ''} onFocus={(e) => e.target.select()} style={{
            ...mono, backgroundColor: 'var(--background-secondary)',
            color: 'var(--text-primary)', borderColor: 'var(--border-color)'
          }} />

          <Label className="mt-3" style={{ color: 'var(--text-primary)' }}>{t('agent_access_prompt')}</Label>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 8 }}>
            {t('agent_access_prompt_help')}
          </p>
          <Input type="textarea" rows={14} readOnly
                 value={issued ? buildAgentPrompt(issued.connection, issued.username, issued.password, issued.media_token) : ''}
                 style={{
                   ...mono, backgroundColor: 'var(--background-secondary)',
                   color: 'var(--text-primary)', borderColor: 'var(--border-color)'
                 }} />

          <Button color="primary" size="sm" className="mt-2"
                  onClick={() => copy(buildAgentPrompt(issued.connection, issued.username, issued.password, issued.media_token))}>
            {copied ? t('agent_access_copied') : t('agent_access_copy_prompt')}
          </Button>

        </ModalBody>
        <ModalFooter>
          <Button color="primary" disabled={!copied} onClick={() => setIssued(null)}>
            {t('agent_access_saved_it')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Personal MCP token for the signed-in admin. Shown once; closing is gated on
          copying, because a lost token can only be replaced by minting another. */}
      <Modal isOpen={!!mcpToken} backdrop="static" keyboard={false} centered size="lg">
        <ModalHeader style={{ color: 'var(--text-primary)' }}>
          {t('agent_access_mcp_token_title')} {mcpToken?.tenant_label}
        </ModalHeader>
        <ModalBody>
          <Alert color="warning">{t('agent_access_mcp_token_warning')}</Alert>

          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            {t('agent_access_mcp_token_help')}
          </p>

          <Label style={{ color: 'var(--text-primary)' }}>{t('agent_access_mcp_token_command')}</Label>
          <Input type="textarea" rows={4} readOnly value={mcpToken?.claude_code_command || ''}
                 onFocus={(e) => e.target.select()} style={{
                   ...mono, backgroundColor: 'var(--background-secondary)',
                   color: 'var(--text-primary)', borderColor: 'var(--border-color)'
                 }} />
          <Button color="primary" size="sm" className="mt-2"
                  onClick={() => copyMcp(mcpToken.claude_code_command)}>
            {mcpCopied ? t('agent_access_copied') : t('agent_access_mcp_token_copy_command')}
          </Button>

          <details className="mt-4">
            <summary style={{ color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>
              {t('agent_access_mcp_token_other_clients')}
            </summary>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '8px 0' }}>
              {t('agent_access_mcp_token_other_clients_help')}
            </p>
            <Label style={{ color: 'var(--text-primary)' }}>URL</Label>
            <Input readOnly value={mcpToken?.mcp_url || ''} style={{
              ...mono, backgroundColor: 'var(--background-secondary)',
              color: 'var(--text-primary)', borderColor: 'var(--border-color)'
            }} />
            <Label className="mt-2" style={{ color: 'var(--text-primary)' }}>{t('agent_access_mcp_token_label')}</Label>
            <Input type="textarea" rows={3} readOnly value={mcpToken?.token || ''}
                   onFocus={(e) => e.target.select()} style={{
                     ...mono, backgroundColor: 'var(--background-secondary)',
                     color: 'var(--text-primary)', borderColor: 'var(--border-color)'
                   }} />
            <Button color="secondary" size="sm" className="mt-2" onClick={() => copyMcp(mcpToken.token)}>
              {t('agent_access_mcp_token_copy_token')}
            </Button>
          </details>

          <p className="mt-3 mb-0" style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            {t('agent_access_mcp_token_expires')} {mcpToken?.expires_at?.slice(0, 10)}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" disabled={!mcpCopied} onClick={() => setMcpToken(null)}>
            {t('agent_access_mcp_token_saved_it')}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!confirmRevoke} toggle={() => setConfirmRevoke(null)} centered>
        <ModalHeader toggle={() => setConfirmRevoke(null)} style={{ color: 'var(--text-primary)' }}>
          {t('agent_access_revoke_title')}
        </ModalHeader>
        <ModalBody style={{ color: 'var(--text-primary)' }}>
          {t('agent_access_revoke_body')} <strong style={mono}>{confirmRevoke?.ch_username}</strong>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmRevoke(null)}>{t('agent_access_cancel')}</Button>
          <Button color="danger" disabled={busy} onClick={handleRevoke}>{t('agent_access_revoke')}</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AdminAgentAccess;
